import InvestNowButton from "assets/svgComponents/buyTokenForm/InvestNowButton";
import clsx from "clsx";
import { paymentMethods } from "constants/methods";
import _ from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  investButtonClass,
  shadowAndBorderClass,
} from "screens/Dashboard/utils/classes";
import { PromocodeValidationResponse, tokensApi } from "services/tokens";
import { appStore } from "store";
import { useUserStore } from "store/user";
import { HeroFormType, IPaymentLimit } from "types/Tokens";
import { Button } from "ui-kit/Button";
import {
  ClaimStatus,
  calcAmountOfTokens,
  calcAmountOfTokensToShow,
  calcStatus,
} from "utils";
import { useAccount } from "wagmi";
import attsLogo from "../../../../assets/img/attsLogo.png";
import { AttsIcon } from "assets/svgComponents/PresaleBlock/ConnectWallet copy";
import { PaymentDialog } from "../PaymentDialogs";
import { PaymentSelect } from "../PaymentSelect";

import { useConnectModal } from "@rainbow-me/rainbowkit";

import { CheckmarkIcon } from "assets/svgComponents/buyTokenForm/CheckIcon";
import { PlusIcon } from "assets/svgComponents/RefCode/PlusIcon";
import { WrongCodeIcon } from "assets/svgComponents/RefCode/WrongCode";

import { AxiosError } from "axios";
import { Loader } from "components/Loader";

import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { refcodes } from "screens/Landing/constants";
import { Step } from "../PaymentDialogs/scheme";

export function HeroForm({
  isStartPayment,
  setIsStartPayment,
}: {
  isStartPayment: boolean;
  setIsStartPayment: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { t } = useTranslation();
  const { currentRound, currentBonus, currentReferralProgram } = appStore();
  const { user } = useUserStore();
  const { openConnectModal } = useConnectModal();
  const investRef = useRef<HTMLInputElement>(null);
  const { address } = useAccount();
  const basePlaceholder = t("heroForm.enterAmount");
  const [placeholder, setPlaceholder] = useState(basePlaceholder);
  const [isFocused, setIsFocused] = useState(false);
  const form = useForm<HeroFormType>({
    defaultValues: {
      step: Step.Confirm,
      value: "",
      referralCode: "",
      promoCode: "",
      paymentMethod: paymentMethods[0],
    },
  });
  const accessToken = localStorage.getItem("accessToken");

  const [isValidPromoData, setIsValidPromoData] =
    useState<PromocodeValidationResponse | null>(null);
  const [isPromoLoading, setIsPromoLoading] = useState(false);

  const checkPromoValidity = async (code: string) => {
    if (!code) {
      clearErrors("promoCode");
      setIsValidPromoData(null);
      return;
    }

    clearErrors("promoCode");
    setIsPromoLoading(true);

    try {
      const data = await tokensApi.validatePromocode(code);

      if (data && data.isValid) {
        setIsValidPromoData(data);
      } else {
        setIsValidPromoData(null);
        setError(
          "promoCode",
          {
            type: "manual",
            message: "Invalid promocode",
          },
          { shouldFocus: false },
        );
        toast.error("Invalid promocode");
      }
    } catch (error) {
      setIsValidPromoData(null);
      setError(
        "promoCode",
        {
          type: "apiError",
          message: "Validation service unavailable",
        },
        { shouldFocus: false },
      );
    } finally {
      setIsPromoLoading(false);
    }
  };

  const debouncedPromoCheck = useCallback(
    _.debounce(checkPromoValidity, 1000),
    [],
  );

  const location = useLocation();
  const pathname = location.pathname;

  const isDashboard = pathname === "/dashboard";

  const handleFocusInvest = () => {
    if (investRef.current) {
      investRef.current.focus();
    }
  };
  const value = parseFloat(form.watch("value")?.replace(",", "."));
  const paymentMethod = form.watch("paymentMethod");
  const referralCodeInForm = form.watch("referralCode");
  const promoCodeInForm = form.watch("promoCode");

  const localSroredrefCode = localStorage.getItem("referralCode");

  const isStableCoin =
    paymentMethod.currency.toLowerCase() === "usdt" ||
    paymentMethod.currency.toLowerCase() === "usdc";

  const { setError, clearErrors } = form;
  const { errors } = form.formState;

  const checkValidity = async (code: string) => {
    if (!code) {
      clearErrors("referralCode");
      return;
    }

    clearErrors("referralCode");

    try {
      const isValid = await tokensApi.isRefCodeValid(code);

      const isUsersCode =
        user.referralCode?.code.toLowerCase() === code.toLowerCase();

      if (isUsersCode) {
        setError(
          "referralCode",
          {
            type: "manual",
            message: t("heroForm.errors.invalidReferralCode"),
          },
          { shouldFocus: false },
        );
        toast.error(t("heroForm.errors.cannotUseOwnCode"));
        localStorage.removeItem("referralCode");
      }

      if (!isValid) {
        setError(
          "referralCode",
          {
            type: "manual",
            message: t("heroForm.errors.invalidReferralCode"),
          },
          { shouldFocus: false },
        );
        localStorage.removeItem("referralCode");
      }
    } catch (error) {
      setError(
        "referralCode",
        {
          type: "apiError",
          message: t("heroForm.errors.validationUnavailable"),
        },
        { shouldFocus: false },
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const validCharsOnly = inputValue
      ?.replace(/[^0-9.]/g, "")
      .replace(",", ".");

    const parts = validCharsOnly.split(".");

    if (parts.length > 2) return;

    if (parts[0].length > 1 && parts[0].startsWith("0")) return;

    const decimal = paymentMethod.decimals;
    if (
      parts.length === 2 &&
      decimal !== undefined &&
      parts[1].length > decimal
    ) {
      return;
    }

    const trimmedVal = validCharsOnly.slice(0, 16);

    form.setValue("value", trimmedVal, { shouldValidate: true });
  };

  const [paymentLimit, setPaymentLimit] = useState<IPaymentLimit | null>(null);
  const valueInUsd = isStableCoin
    ? !!value
      ? value
      : 0
    : Number(
        ((paymentLimit?.usdPrice || 0) * (!!value ? value : 0)).toFixed(2),
      );

  const debouncedCheck = useCallback(_.debounce(checkValidity, 1000), []);

  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);

  const [isLoadingPaymentLimit, setIsLoadingPaymentLimit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isSaleEnded = calcStatus() !== ClaimStatus.OngoingSale;
  const { isConnected } = useAccount();

  const savedReferrerCode = user.referrerCode;

  const isDisableReferralInput =
    !!savedReferrerCode || (!errors.referralCode && !!localSroredrefCode);

  useEffect(() => {
    if (
      isConnected &&
      address &&
      referralCodeInForm &&
      !savedReferrerCode &&
      user.id &&
      accessToken
    ) {
      debouncedCheck(referralCodeInForm);
    }

    return () => {
      debouncedCheck.cancel();
    };
  }, [
    referralCodeInForm,
    debouncedCheck,
    isConnected,
    savedReferrerCode,
    accessToken,
  ]);

  useEffect(() => {
    if (isConnected && address && promoCodeInForm && accessToken) {
      debouncedPromoCheck(promoCodeInForm);
    } else {
      setIsValidPromoData(null);
    }

    return () => {
      debouncedPromoCheck.cancel();
    };
  }, [promoCodeInForm, debouncedPromoCheck, isConnected, address, accessToken]);

  const promoPercent = isValidPromoData?.bonusPercentage || 0;

  const youReceiveBase = calcAmountOfTokensToShow(
    valueInUsd,
    currentRound,
    false,
  );

  const promoBonusTokens =
    promoPercent && Number(youReceiveBase)
      ? Number(youReceiveBase) * (promoPercent / 100)
      : 0;

  const hasInvalidReferralCode = referralCodeInForm && errors.referralCode;

  const hasInvalidPromoCode = promoCodeInForm && errors.promoCode;
  const isPromoValidAndEntered =
    promoCodeInForm && isValidPromoData && !hasInvalidPromoCode;

  const promoBonusToShow = promoBonusTokens.toFixed(2);

  const isAmountLessThanMin =
    valueInUsd > 0 && valueInUsd < paymentMethod.minAmount;

  const isDisabledBuyButton =
    user.kicked ||
    !currentRound ||
    !!hasInvalidReferralCode ||
    !!hasInvalidPromoCode ||
    isLoading ||
    isAmountLessThanMin;

  const handleSelectAsset = async () => {
    const tokens = calcAmountOfTokens(
      valueInUsd,
      currentRound?.tokenPrice,
      currentRound?.maxPurchasePerWallet,
    );

    const { paymentMethod } = form.getValues();
    setIsLoading(true);
    try {
      const res = await tokensApi.sendOrder(
        tokens,
        paymentMethod.nowPaymentsId,
        referralCodeInForm,
        isPromoValidAndEntered ? promoCodeInForm : undefined,
      );

      if (!res) {
        throw new Error(t("heroForm.errors.somethingWentWrong"));
      }

      form.setValue("payAmount", res.invoice.payAmount);
      form.setValue("payAddress", res.invoice.payAddress);
      form.setValue("orderId", res.order.id);

      if (referralCodeInForm && !localSroredrefCode) {
        localStorage.setItem("referralCode", referralCodeInForm);
      }

      handleBuyNow();
    } catch (error) {
      if (error instanceof AxiosError) {
        form.setError("root", {
          type: "custom",
          message: error.response?.data.message,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBuyNow = async () => {
    if (isSaleEnded) {
      return;
    }

    if (!isConnected || !address) {
      openConnectModal?.();
      return;
    }

    setIsPaymentDialogOpen(true);
    form.setValue("step", Step.Confirm);
  };

  const handlePaymentDialogOpen = (val: boolean) => {
    setIsPaymentDialogOpen(val);

    if (!val) form.setValue("value", "");
  };

  const fetchPaymentLimit = useCallback(async (token: string) => {
    try {
      setIsLoadingPaymentLimit(true);
      const data = await tokensApi.getPaymentLimit(token);
      if (data) {
        setPaymentLimit(data);
      }
    } catch (error) {
      console.warn(error);
      toast.error(
        `Failed to get price for ${paymentMethod.currency} in ${paymentMethod.network}`,
      );
      setPaymentLimit(null);
    } finally {
      setIsLoadingPaymentLimit(false);
    }
  }, []);

  useEffect(() => {
    if (!value || !paymentLimit) {
      fetchPaymentLimit(paymentMethod.nowPaymentsId);
    }
  }, [paymentMethod.currency, value]);

  useEffect(() => {
    if (savedReferrerCode) {
      form.setValue("referralCode", savedReferrerCode);
    } else if (localSroredrefCode) {
      form.setValue("referralCode", localSroredrefCode);
    }
  }, [savedReferrerCode, localSroredrefCode, form.setValue]);

  useEffect(() => {
    setIsStartPayment(isPaymentDialogOpen);
  }, [isPaymentDialogOpen]);

  const isCodeValidAndEntered = referralCodeInForm && !hasInvalidReferralCode;

  const specializedRef = refcodes.includes(referralCodeInForm);

  const refPercent = specializedRef
    ? 10
    : currentReferralProgram?.refereeBonusPercentAmount;
  const referralBonusToShow =
    refPercent && youReceiveBase
      ? (Number(youReceiveBase) * (refPercent / 100))?.toFixed(2)
      : "0";

  return (
    <FormProvider {...form}>
      {isPaymentDialogOpen ? (
        <PaymentDialog
          open={isPaymentDialogOpen}
          onOpenChange={handlePaymentDialogOpen}
          paymentInUSD={valueInUsd}
          paymentMethod={paymentMethod}
          setIsPaymentDialogOpen={setIsPaymentDialogOpen}
          referralCode={referralCodeInForm}
          promoCodePercentage={promoPercent}
        />
      ) : (
        <form
          onSubmit={form.handleSubmit(handleSelectAsset)}
          className={clsx(
            "flex flex-col w-[421px] max-lg:gap-[16px] max-lg:w-full gap-[16px] bg-[#1a1a1a] max-lg:rounded-[16px] max-lg:before:!rounded-[16px] rounded-[16px] before:!rounded-[16px] p-[8px] max-lg:p-[8px]",
            shadowAndBorderClass,
            isDashboard ? " max-lg:justify-between max-lg:flex-1" : "",
          )}
          autoComplete="off"
        >
          <div className="flex flex-col">
            <div className="text-[14px] max-lg:text-14m p-[4px] max-lg:p-[4px] text-white/40 leading-[110%] font-luxora-regular">
              {t("heroForm.youInvest")}
            </div>
            <div
              className={clsx(
                "flex items-center lg:justify-between before:!rounded-[12px] rounded-[12px] max-lg:rounded-[12px] max-lg:before:!rounded-[12px] bg-[#252525] max-lg:p-[4px] max-lg:pl-[12px] py-[4px] pl-[12px] pr-[4px]",
                shadowAndBorderClass,
              )}
            >
              <div
                onClick={handleFocusInvest}
                className="h-[49px] max-lg:w-[50%] max-lg:h-[41px] flex items-center"
              >
                <Controller
                  name="value"
                  control={form.control}
                  disabled={isSaleEnded || !address}
                  rules={{
                    required: t("heroForm.errors.fieldRequired"),
                  }}
                  render={({ field }) => {
                    const handleFocus = () => {
                      setPlaceholder(t("heroForm.zeroAmount"));
                      setIsFocused(true);
                    };

                    const handleBlur = () => {
                      setPlaceholder(t("heroForm.enterAmount"));
                      setIsFocused(false);
                    };

                    return (
                      <div className="flex flex-col">
                        <input
                          type="text"
                          className={clsx(
                            "autofill-dark !bg-[#252525] max-lg:w-full outline-none font-luxora-regular leading-[110%] placeholder:text-[16px] text-[16px] text-white placeholder:text-white/25",
                            value.toString().length < 12
                              ? "max-lg:text-14m max-lg:placeholder:text-14m"
                              : "max-lg:text-[16px] max-lg:placeholder:text-[16px]",
                          )}
                          placeholder={placeholder}
                          {...field}
                          ref={investRef}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          onChange={handleAmountChange}
                        />
                        {isFocused && (
                          <div
                            className={`text-[12px] ${
                              value.toString().length < 12
                                ? "max-lg:text-12m "
                                : "max-lg:text-[11px] "
                            }  leading-[120%] text-white/60`}
                          >
                            $
                            {value && paymentLimit?.usdPrice
                              ? (value * paymentLimit?.usdPrice).toFixed(2)
                              : "0.00"}
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
              </div>

              <div
                className={clsx(
                  "rounded-[12px] before:rounded-[12px] max-lg:rounded-[12px] max-lg:before:!rounded-[12px]",
                )}
              >
                <Controller
                  name="paymentMethod"
                  control={form.control}
                  render={({ field }) => {
                    return (
                      <PaymentSelect
                        selected={field.value}
                        onSelect={(val) => {
                          form.setValue("value", "");
                          field.onChange(val);
                        }}
                        type={"preselect"}
                      />
                    );
                  }}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col">
            <div className="text-[14px] max-lg:text-14m p-[4px] max-lg:p-[4px] text-white/40 leading-[110%] font-luxora-regular">
              {t("heroForm.youReceive")}
            </div>
            <div className="flex items-center justify-between border-[2px] max-lg:border-[2px] rounded-[16px] max-lg:rounded-[12px] py-[6px] max-lg:py-[6px] pl-[6px] max-lg:pl-[6px] pr-[16px] max-lg:pr-[16px] border-[#252525]">
              <div className="flex items-center gap-[3px]">
                <div
                  className={clsx(
                    "w-[29px] h-[29px] max-lg:w-[29px] max-lg:h-[29px] rounded-full before:!rounded-full flex items-center justify-center shrink-0",
                    shadowAndBorderClass,
                  )}
                >
                  <AttsIcon className="w-[29px] h-[29px] max-lg:w-[29px] max-lg:h-[29px]" />
                </div>
                <div className="text-[16px] max-lg:text-14m font-luxora-regular leading-normal text-white">
                  ATTS
                </div>
              </div>
              <div className="text-[16px] font-luxora-regular max-lg:text-14m leading-normal text-white">
                {youReceiveBase || 0}
              </div>
            </div>
          </div>

          {valueInUsd > 0 && valueInUsd < paymentMethod.minAmount && (
            <div
              className="mt-1 rounded border border-red-500/40 bg-red-500/10 px-4 py-1 text-center text-[12px] font-notmal text-red-200"
              role="alert"
            >
              {t("payment.minimumAmount", {
                currency: paymentMethod.currency,
                network: paymentMethod.network,
                amount: paymentMethod.minAmount.toFixed(2),
              })}
            </div>
          )}
          <div className="flex flex-row items-start w-full gap-[12px] max-lg:gap-[8px]">
            <div className="flex flex-col w-full ">
              <div className="text-[14px] max-lg:text-14m p-[4px] max-lg:p-[4px] text-white/40 leading-[110%] font-luxora-regular">
                {t("heroForm.referralCode")}
              </div>
              <div
                className={clsx(
                  "flex w-full h-[48px] max-lg:h-[48px] rounded-[12px] max-lg:rounded-[12px] before:!rounded-[12px] max-lg:before:!rounded-[12px] flex-col",
                  shadowAndBorderClass,
                )}
              >
                <div className="relative w-full">
                  <div className="absolute right-[8px] max-lg:right-[8px] top-[7px] max-lg:top-[8px] flex items-center justify-center bg-[#333333] rounded-[8px] max-lg:rounded-[8px] shadow-md w-[32px] h-[32px] max-lg:w-[32px] max-lg:h-[32px]">
                    {isCodeValidAndEntered ? (
                      <CheckmarkIcon className="max-lg:w-[23px] max-lg:h-[23px]" />
                    ) : hasInvalidReferralCode ? (
                      <WrongCodeIcon className="max-lg:w-[23px] max-lg:h-[23px]" />
                    ) : (
                      <PlusIcon className="max-lg:w-[23px] max-lg:h-[23px]" />
                    )}
                  </div>

                  <Controller
                    name="referralCode"
                    control={form.control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="text"
                        placeholder={t("heroForm.enterCode")}
                        disabled={isDisableReferralInput}
                        className={clsx(
                          "w-full h-[48px] max-lg:h-[48px] px-[1em] rounded-[12px] max-lg:rounded-[12px] bg-[#252525] placeholder:font-[400] placeholder:text-[16px] max-lg:placeholder:text-14m max-lg:text-14m text-[16px] placeholder:leading-[110%] placeholder:text-white/25 outline-none",

                          "transition-all duration-300 ease-in-out border-2 border-transparent",

                          "border-transparent",
                          hasInvalidReferralCode && "text-[#FF7676]",
                        )}
                      />
                    )}
                  />
                </div>
              </div>
              {hasInvalidReferralCode && (
                <p className="pl-[12px] text-14 max-lg:text-12m mt-[8px] max-lg:pl-[12px] max-lg:mt-[12px] font-luxora-regular text-[#FF7676]">
                  {t("heroForm.errors.wrongReferralCode")}
                </p>
              )}
            </div>
            <div className="flex flex-col w-full">
              <div className="text-[14px] max-lg:text-14m p-[4px] max-lg:p-[4px] text-white/40 leading-[110%] font-luxora-regular">
                {t("heroForm.bonusAtts")} ({refPercent || 0}
                %)
              </div>

              <div
                className={`flex items-center ${
                  referralBonusToShow.length < 9
                    ? "pr-[16px] max-lg:pr-[16px]"
                    : "pr-[7px] max-lg:pr-[8px]"
                } lg:justify-between border-[2px] max-lg:border-[2px] rounded-[16px]
                  max-lg:rounded-[16px] py-[6px] max-lg:py-[6px] pl-[6px]
               max-lg:pl-[6px] border-[#252525]`}
              >
                <div className="flex items-center gap-[3px]">
                  <div
                    className={clsx(
                      "w-[29px] h-[29px] max-lg:w-[29px] max-lg:h-[29px] rounded-full before:!rounded-full flex items-center justify-center shrink-0",
                      shadowAndBorderClass,
                    )}
                  >
                    <AttsIcon className="w-[29px] h-[29px] max-lg:w-[29px] max-lg:h-[29px]" />
                  </div>
                  <div className="text-[16px] max-lg:hidden max-lg:text-14m font-luxora-regular leading-normal text-white">
                    ATTS
                  </div>
                </div>
                <div
                  className={` max-w-[90px] max-lg:max-w-[76px] leading-[106%] ${
                    referralBonusToShow.length < 9
                      ? "text-[16px] max-lg:text-14m"
                      : "text-[10px] max-lg:text-12m break-all"
                  }
                 font-luxora-regular  leading-normal text-white`}
                >
                  {referralCodeInForm && !errors.referralCode
                    ? referralBonusToShow
                    : 0}
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-row items-start w-full gap-[12px] max-lg:gap-[8px]">
            <div className="flex flex-col w-full ">
              <div className="text-[14px] max-lg:text-14m p-[4px] max-lg:p-[4px] text-white/40 leading-[110%] font-luxora-regular">
                Promocode
              </div>
              <div
                className={clsx(
                  "flex w-full h-[48px] max-lg:h-[48px] rounded-[12px] max-lg:rounded-[12px] before:!rounded-[12px] max-lg:before:!rounded-[12px] flex-col",
                  shadowAndBorderClass,
                )}
              >
                <div className="relative w-full">
                  <div className="absolute right-[8px] max-lg:right-[8px] top-[7px] max-lg:top-[8px] flex items-center justify-center bg-[#333333] rounded-[8px] max-lg:rounded-[8px] shadow-md w-[32px] h-[32px] max-lg:w-[32px] max-lg:h-[32px]">
                    {isPromoLoading ? (
                      <Loader className="max-lg:w-[23px] max-lg:h-[23px]" />
                    ) : isPromoValidAndEntered ? (
                      <CheckmarkIcon className="max-lg:w-[23px] max-lg:h-[23px]" />
                    ) : hasInvalidPromoCode ? (
                      <WrongCodeIcon className="max-lg:w-[23px] max-lg:h-[23px]" />
                    ) : (
                      <PlusIcon className="max-lg:w-[23px] max-lg:h-[23px]" />
                    )}
                  </div>

                  <Controller
                    name="promoCode"
                    control={form.control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="text"
                        placeholder={t("heroForm.enterCode")}
                        className={clsx(
                          "w-full h-[48px] max-lg:h-[48px] px-[1em] rounded-[12px] max-lg:rounded-[12px] bg-[#252525] placeholder:font-[400] placeholder:text-[16px] max-lg:placeholder:text-14m max-lg:text-14m text-[16px] placeholder:leading-[110%] placeholder:text-white/25 outline-none",
                          "transition-all duration-300 ease-in-out border-2 border-transparent",
                          "border-transparent",
                          hasInvalidPromoCode && "text-[#FF7676]",
                        )}
                      />
                    )}
                  />
                </div>
              </div>
              {hasInvalidPromoCode && (
                <p className="pl-[12px] text-14 max-lg:text-12m mt-[8px] max-lg:pl-[12px] max-lg:mt-[12px] font-luxora-regular text-[#FF7676]">
                  {errors.promoCode?.message ||
                    t("heroForm.errors.wrongPromocode")}
                </p>
              )}
            </div>

            <div className="flex flex-col w-full">
              <div className="text-[14px] max-lg:text-14m p-[4px] max-lg:p-[4px] text-white/40 leading-[110%] font-luxora-regular">
                Promo Bonus ({promoPercent || 0}%)
              </div>

              <div
                className={`flex items-center ${
                  promoBonusToShow.length < 9
                    ? "pr-[16px] max-lg:pr-[16px]"
                    : "pr-[7px] max-lg:pr-[8px]"
                } lg:justify-between border-[2px] max-lg:border-[2px] rounded-[16px]
                  max-lg:rounded-[16px] py-[6px] max-lg:py-[6px] pl-[6px]
               max-lg:pl-[6px] border-[#252525]`}
              >
                <div className="flex items-center gap-[3px]">
                  <div
                    className={clsx(
                      "w-[29px] h-[29px] max-lg:w-[29px] max-lg:h-[29px] rounded-full before:!rounded-full flex items-center justify-center shrink-0",
                      shadowAndBorderClass,
                    )}
                  >
                    <AttsIcon className="w-[29px] h-[29px] max-lg:w-[29px] max-lg:h-[29px]" />
                  </div>
                  <div className="text-[16px] max-lg:hidden max-lg:text-14m font-luxora-regular leading-normal text-white">
                    ATTS
                  </div>
                </div>
                <div
                  className={` max-w-[90px] max-lg:max-w-[76px] leading-[106%] ${
                    promoBonusToShow.length < 9
                      ? "text-[16px] max-lg:text-14m"
                      : "text-[10px] max-lg:text-12m break-all"
                  }
                 font-luxora-regular  leading-normal text-white`}
                >
                  {isPromoValidAndEntered ? promoBonusToShow : 0}
                </div>
              </div>
            </div>
          </div>
          <div className="flex gap-[8px] max-lg:mt-[11px]">
            <Button
              className={investButtonClass}
              disabled={isDisabledBuyButton}
            >
              <div
                className={`absolute flex ${
                  !isLoading && "hidden"
                } items-center justify-center w-full h-full
                 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 `}
              >
                {" "}
                <Loader />
              </div>

              <span className="max-lg:text-16m text-[16px] leading-[110%] font-luxora-medium text-black">
                {t("heroForm.investNow")}
              </span>
              <div className="bg-[#F3F3F8] max-lg:rounded-[8px] max-lg:w-[32px] max-lg:h-[32px] w-[32px] h-[32px] rounded-[8px] flex items-center justify-center shrink-0">
                <InvestNowButton className="size-[18px]" />
              </div>
            </Button>
          </div>
        </form>
      )}
    </FormProvider>
  );
}
